import { useState, useCallback } from 'react';
import Video from 'twilio-video';

export const useScreenShare = (room, selectedVideoDeviceId) => {
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [screenTrack, setScreenTrack] = useState(null);

  const stopScreenShare = useCallback(async () => {
    if (!screenTrack || !room?.localParticipant) return;

    try {
      room.localParticipant.unpublishTrack(screenTrack);
      if (typeof screenTrack.stop === 'function') {
        screenTrack.stop();
      }
      setScreenTrack(null);
      
      // Re-enable camera
      const tracks = await Video.createLocalTracks({ 
        video: { deviceId: { exact: selectedVideoDeviceId } } 
      });
      const newCameraTrack = tracks.find(t => t.kind === 'video');
      if (newCameraTrack) {
        room.localParticipant.publishTrack(newCameraTrack);
      }
      
      setIsScreenSharing(false);
    } catch (err) {
      console.error('Error stopping screen share:', err);
    }
  }, [room, screenTrack, selectedVideoDeviceId]);

  const startScreenShare = useCallback(async () => {
    if (!room || !room.localParticipant) return;

    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
      const newScreenTrack = new Video.LocalVideoTrack(stream.getTracks()[0]);
      setScreenTrack(newScreenTrack);
      
      // Unpublish camera video track
      const videoTracks = Array.from(room.localParticipant.videoTracks.values());
      videoTracks.forEach(publication => {
        room.localParticipant.unpublishTrack(publication.track);
        if (publication.track && typeof publication.track.stop === 'function') {
          publication.track.stop();
        }
      });
      
      // Publish screen track
      room.localParticipant.publishTrack(newScreenTrack);
      setIsScreenSharing(true);
      
      // Listen for when the user stops sharing from browser UI
      newScreenTrack.mediaStreamTrack.onended = () => {
        stopScreenShare();
      };
    } catch (err) {
      console.error('Screen share error:', err);
    }
  }, [room, stopScreenShare]);

  const toggleScreenShare = useCallback(async () => {
    if (!isScreenSharing) {
      await startScreenShare();
    } else {
      await stopScreenShare();
    }
  }, [isScreenSharing, startScreenShare, stopScreenShare]);

  return {
    isScreenSharing,
    screenTrack,
    startScreenShare,
    stopScreenShare,
    toggleScreenShare
  };
}; 